import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { callNotify } from '../../../../..';
import Certain from '../../../../../components/Operation/Certain';
import Logo from '../../../../../components/Operation/Logo';
import { callEventOperation, getOperation, setOperation, useOperation } from '../../../../../contexts/OperationContext';
import { callEventPlan, getPlan, setPlan } from '../../../../../contexts/PlanOperationContext/Plan';
import { openPopup2 } from '../../../../../contexts/PopupContext';
import { selectOpMode } from '../../../../../util/store/selectors';
import { getStore, useSelector } from "../../../../../util/store/store";
import { ACTION_TYPE, eventType, OPERATION_TYPE, serviceTask, SITE_TYPE } from "../../../../../util/varibles/constants";
import { cloneObj, getStartFinishByOpMode, removeClassName } from "../../../../../util/varibles/global";
import { ILevel, IOperation, ITaskService } from "../../../../../util/varibles/interface";
import { getTypesAndUnitOfTreatment } from '../../../../PlanOperationPage/util';
import { PlanOperationState, selectPlan } from '../../../constants';
import PopupMerge from '../../../Popup/OpMerge';
import { planOpActions } from '../../../reducer';
import { updateCrewTasksTime, updateTime } from '../../../util/function_operation/global';
import { EL_RULE } from '../../Calendar/constants';
import { checkStatus, getNewTimeHorizontal } from '../../Calendar/Operations/Operation/_components/constants';
import LoadingOperation from '../../Calendar/Operations/Operation/_components/WrapperOperation/LoadingOperation';
import styles from '../style.module.scss';
import Timeline from './Timeline';
import { HEIGHT_PER_OP } from './Vessels';

const descriptionView: Record<string, (data: IOperation) => { edit: () => Partial<PlanOperationState['popupOperation']>, Node: ReactNode }> = {
    [OPERATION_TYPE.EVENT]: (data) => {
        const { event_type } = data.operation;
        const { name = '' } = eventType[event_type] || {}
        return {
            edit: () => ({
                isShow: true,
                operationType: OPERATION_TYPE.EVENT,
                editMode: ACTION_TYPE.EDIT,
                values: data,
            }),
            Node: <div className='text-nowrap'>{name}</div>
        };
    },
    [OPERATION_TYPE.HARVEST]: (data) => {
        const { operation, activity_log, vessel } = data;
        return {
            edit: () => ({
                isShow: true,
                operationType: OPERATION_TYPE.HARVEST,
                editMode: ACTION_TYPE.EDIT,
                values: {
                    operation,
                    activity_log,
                    vessel
                }
            }),
            Node: <>
                <div className='text-nowrap'>{operation.site_name}</div>
                <div className='arrow-right' />
                <div className='text-nowrap'>{operation.factory_name}</div>
            </>
        }
    },
    [OPERATION_TYPE.SERVICE]: (data) => {
        const { site_id, site_name, sites = [], tasks = [] } = data.operation;
        const location = sites.length === 0
            ? site_name
            : sites.map((item: any) => item.name).join(' | ')
        const taskTypes = tasks.reduce((rs: { [id: string]: string | undefined }, item: ITaskService) => {
            const { type = "" } = item;
            rs[type] = item.name
            return rs
        }, {})
        const jobNames = Object.keys(taskTypes).map(key => {
            const name = taskTypes[key] || serviceTask[key || ""]?.name || " "
            return name;
        }).join(', ')
        return {
            edit: () => {
                const sites = selectPlan.sites(getStore());
                const { operation, vessel, activity_log } = data;
                const unitsOp = tasks.reduce((rs: any, item: any) => {
                    const { id = '' } = item.sub_operation || {};
                    rs[id] = true;
                    return rs;
                }, {})
                const { units = [], type = SITE_TYPE.INTERNAL } = sites.find((item: any) => item.id === site_id) || {};

                return {
                    isShow: true,
                    operationType: OPERATION_TYPE.SERVICE,
                    editMode: ACTION_TYPE.EDIT,
                    values: {
                        activity_log,
                        operation,
                        vessel,
                        store: Object.keys(unitsOp).reduce((list: any, id: any) => {
                            const key = id + '|-';
                            list[key] = {
                                ...units.find((unit: any) => unit.id === id),
                                site_id,
                                site_name,
                                site_type: type,
                            }
                            return list;
                        }, {})
                    }
                }
            },
            Node: <div className='text-nowrap'>{location} - {jobNames}</div>
        }
    },
    [OPERATION_TYPE.SUPPORT]: () => {
        return {
            edit: () => ({}),
            Node: <div>Support</div>
        }
    },
    [OPERATION_TYPE.TRANSPORT]: (data) => {
        const { operation } = data;
        return {
            edit: () => {
                const sites = selectPlan.sites(getStore());
                const { sub_operations, source_id, source_name } = operation;
                const { units = [] } = sites.find((item: any) => item.id === source_id) || {};

                return {
                    isShow: true,
                    operationType: OPERATION_TYPE.TRANSPORT,
                    editMode: ACTION_TYPE.EDIT,
                    values: {
                        ...cloneObj(data),
                        store: sub_operations.reduce((list: any, { id }: any) => {
                            const key = id + '|-';
                            list[key] = {
                                ...units.find((unit: any) => unit.id === id),
                                site_id: source_id,
                                site_name: source_name,
                            }
                            return list;
                        }, {}),
                    }
                }
            },
            Node: <>
                <div className='text-nowrap'>{operation.source_name}</div>
                <div className='arrow-right' />
                <div className='text-nowrap'>{operation.destination_name}</div>
            </>
        }
    },
    [OPERATION_TYPE.TREATMENT]: (data) => {
        const { site_name } = data.operation;
        const { types } = getTypesAndUnitOfTreatment(data.operation.tasks);
        return {
            edit: () => {
                const { operation, vessel, activity_log } = data;
                return {
                    isShow: true,
                    operationType: OPERATION_TYPE.TREATMENT,
                    editMode: ACTION_TYPE.EDIT,
                    values: {
                        activity_log,
                        operation,
                        vessel
                    }
                }
            },
            Node: <div className='text-nowrap'>{site_name} - {Object.keys(types).map(k => types[k]).join(", ")} ({data.operation.sub_operations.map(item => item.unit_id).join(", ")})</div>
        }
    }
}

interface IProps {
    data: IOperation
    level: ILevel

}

const WrapperOperation: React.FC<IProps> = ({ data, level }) => {
    const dispatch = useDispatch();
    const operation_mode = useSelector(selectOpMode);
    const opFocusId = useSelector(selectPlan.opFocusId);
    const rootRef = useRef<HTMLDivElement>(null);
    const [description, setDescription] = useState<ReactNode | undefined>();
    const [minWidth, setMinWidth] = useState<string | undefined>();
    const { openInfo, resetState, getActivityLog } = callEventOperation();
    const initRef = useRef({
        id: data.operation.id,
        opType: data.operation.operation_type,
        setOp: setOperation(),
        getOp: getOperation(),
        setContainerOfOp: setPlan(),
        getContainerOfOp: getPlan(),
        setTarget: callEventPlan().setTarget,
        openInfo,
        resetState,
        getActivityLog,
        edit: () => { }
    });
    const [top] = useOperation(state => state.top);
    const [vesselId] = useOperation(state => state.vesselId);
    const [modeDrag] = useOperation(state => state.modeDrag);
    const [realStatus] = useOperation(state => state.realStatus);
    const intervalController = useRef<AbortController>(new AbortController());
    const dragController = useRef<AbortController>(new AbortController());
    const intervalMove = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        const el = rootRef.current;
        if (!el)
            return;
        const onMouseDown = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const { getOp, setOp, getContainerOfOp, setContainerOfOp, setTarget, edit } = initRef.current;
            const { isOwn = false, isDrag, data } = getOp();
            const { permission } = getContainerOfOp();
            if (permission.OP_MENU && data)
                setTarget({ type: EL_RULE.OPERATION, data, isOwn, edit }, e);

            if (e.button !== 0 || !rootRef.current)
                return;

            intervalController.current.abort();

            const { calendarRef } = getContainerOfOp();
            if (calendarRef && calendarRef())
                calendarRef().dataset.modeDrag = `${initRef.current.opType}`;

            setContainerOfOp({ isDrag: 'true' });
            document.addEventListener('mouseup', onMouseUp);
            const { status } = data?.operation || {};
            const isStatusValid = checkStatus(status);
            const state: any = { modeDrag: 'down', isTimeline: true };
            dispatch(planOpActions.setFocusId(initRef.current.id));
            const { loading } = getOp();
            if (isStatusValid && isOwn && isDrag && !loading && permission.UPDATE_OP) {
                dragController.current = new AbortController();
                document.addEventListener('mousemove', onMouseMove);
                const { offsetTop, offsetWidth } = rootRef.current;
                state.targetX = e.pageX;
                state.targetY = offsetTop - e.pageY;
                state.maxWidth = (calendarRef && calendarRef())?.scrollWidth || 0 - offsetWidth;
                state.scrollHeight = (calendarRef && calendarRef())?.scrollHeight;
            }
            setOp(state)
        };
        const onMouseMove = (e: MouseEvent) => {
            window.clearInterval(intervalMove.current);
            if (!rootRef.current)
                return;

            const { id, getOp, setOp, getActivityLog } = initRef.current;
            const { id: positionId = null, className = '' } = (e.target as HTMLDivElement || undefined) || {};
            const isOperation = className.indexOf(styles.operation) !== -1;
            if (isOperation && positionId !== id) {
                rootRef.current.style.display = 'none'
                const { modeDrag } = getOp();
                if (modeDrag !== 'move')
                    setOp({ modeDrag: 'move' });
                return;
            }

            const state = { modeDrag: 'move' };
            rootRef.current.style.display = 'block';

            const { targetY = 0, activityLogs } = getOp();
            const { newStartTime = 0, rootKey, currentKey, vessel, y, isAction } = getNewTimeHorizontal({ get: getOp, e });

            if (isAction && vessel && getOp().data?.operation.operation_type !== OPERATION_TYPE.SERVICE) {
                getActivityLog({ time: newStartTime, currentKey, vessel, abort: dragController.current })
            }

            document.body.style.cursor = 'grabbing';
            setOp({
                ...state,
                top: targetY + y,
                rootKey,
                activityLogs: { ...activityLogs, [rootKey]: updateTime(activityLogs[rootKey], 0, newStartTime) }
            });
        }

        const onMouseUp = (e: MouseEvent) => {
            if (!rootRef.current)
                return;

            const { id, getOp, setOp, getContainerOfOp, setContainerOfOp, openInfo, resetState, getActivityLog } = initRef.current;
            const { calendarRef } = getContainerOfOp();
            if (calendarRef && calendarRef())
                calendarRef().dataset.modeDrag = '';

            setContainerOfOp({ isDrag: '' });
            document.body.style.cursor = 'default';
            document.body.setAttribute('data-is-prevent-select', 'false');
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            const { modeDrag, data } = getOp();
            if (modeDrag !== 'move') {
                rootRef.current.style.display = 'block';
                dispatch(planOpActions.setFocusId(id));
                openInfo()
            } else if (data) {
                if (intervalMove.current)
                    window.clearInterval(intervalMove.current);

                setOp({ modeDrag: '', loading: false });
                if (dragController)
                    dragController.current.abort();

                const { pageX, pageY, } = e;
                const { id: positionId = null, className = '' } = (e.target as HTMLDivElement || undefined) || {};
                const isOperation = className.indexOf(styles.operation) !== -1;
                if (isOperation) {
                    removeClassName(e.target, styles['merge-hover']);
                    const cancel = () => {
                        if (rootRef.current)
                            rootRef.current.style.display = 'block';
                        resetState({ isTimeline: true });
                    }

                    if (positionId === id) {
                        cancel();
                        return;
                    }
                    if (data)
                        openPopup2(<PopupMerge idTarget={id} opDrag={data} />, { modalProps: { title: "Merge Operation", width: 860 }, onClose: cancel })

                    return;
                }

                const { newStartTime, currentKey, vessel, error, isAction, isCheck } = getNewTimeHorizontal({ get: getOp, e });
                if (!vessel) {
                    resetState({});
                    return;
                }

                if (error.length > 0) {
                    if (error !== '-')
                        callNotify?.error({ title: 'Error', description: error });
                    resetState({});
                    return;
                }
                const { operation } = data;
                let available_time_id: string = '-', contract_id = '-';
                if (!vessel.isOwn) {
                    available_time_id = operation.available_time_id || '-';
                    contract_id = operation.contract_id || '-';
                }
                rootRef.current.style.display = 'none';
                let location = document.elementFromPoint(pageX, pageY) as HTMLElement;
                if (location) {
                    const { rule, availableTimeId, contractId } = location.dataset;
                    if (rule === EL_RULE.AVAILABLE_TIME)
                        available_time_id = availableTimeId || '-'
                    else if (rule === EL_RULE.CONTRACT)
                        contract_id = contractId || '-';
                }
                rootRef.current.style.display = 'block';

                const valueNew = {
                    operation: {
                        ...operation,
                        ...(operation.operation_type === OPERATION_TYPE.SERVICE ? {
                            crew_tasks: updateCrewTasksTime(
                                operation.crew_tasks,
                                newStartTime - data.activity_log[0].est_start_time
                            )
                        } : {}),
                        contract_id,
                        available_time_id,
                        vessel_id: vessel?.id,
                    },
                    vessel
                }

                setOp({ top: undefined, vesselId: vessel.id });
                const updateOpEffect = (new_operation: IOperation) => {
                    const { onDragOp } = getContainerOfOp();
                    onDragOp(new_operation);
                }
                if (operation.operation_type !== OPERATION_TYPE.SERVICE
                    && (isAction || (isCheck && data.activity_log[0].est_start_time !== newStartTime))) {
                    getActivityLog({
                        time: newStartTime,
                        vessel,
                        success: (ops) => {
                            const activityLogNew = ops[id].activity_log;
                            const op = {
                                ...valueNew,
                                operation: ops[id].operation,
                                activity_log: activityLogNew,
                            };
                            op.operation.duration = activityLogNew[activityLogNew.length - 1].est_start_time - activityLogNew[0].est_start_time;
                            updateOpEffect(op);
                            setOp({ loading: false });
                        },
                    })
                } else {
                    const { activityLogs } = getOp();
                    const activity_log = updateTime(activityLogs[currentKey] || data.activity_log, 0, newStartTime);
                    const op = { ...valueNew, activity_log, vessel };
                    updateOpEffect(op)
                }
            }
        }
        el.addEventListener('mousedown', onMouseDown)
        return () => {
            el.removeEventListener('mousedown', onMouseDown)
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        }
    }, [dispatch]);

    useEffect(() => {
        if (opFocusId !== data.operation.id) {
            return;
        }

        const { getContainerOfOp } = initRef.current;
        const { calendarRef } = getContainerOfOp();
        const planRef = calendarRef?.();

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest(`.${styles.timeline}`)) {
                return;
            }

            if (
                rootRef.current &&
                !rootRef.current.contains(target) &&
                !target.closest('.ant-dropdown')
            ) {
                dispatch(planOpActions.setFocusId(''));
            }
        };

        planRef?.addEventListener('mousedown', handleClickOutside);

        return () => {
            planRef?.removeEventListener('mousedown', handleClickOutside);
        };
    }, [data.operation.id, dispatch, opFocusId]);

    useEffect(() => {
        const { [`${data.operation.operation_type}`]: descriptionFn } = descriptionView || {};
        const { edit, Node } = descriptionFn?.(data);
        initRef.current.edit = edit;
        setDescription(Node);
        setTimeout(() => {
            if (rootRef.current) {
                const { childNodes } = rootRef.current;
                let minWidth = 12;
                const list = Array.from(childNodes);
                list.pop();
                list.forEach(element => {
                    const { offsetWidth } = element as HTMLElement;
                    minWidth += (offsetWidth + 2);
                });
                setMinWidth(`${minWidth}px`)
            }
        }, 10)
    }, [data])

    const [activityLog = data.activity_log] = useOperation(state => state.activityLogs[state.rootKey]);
    const { start, finish } = getStartFinishByOpMode[operation_mode](activityLog, data.operation.status);


    const moreHeightOfOp = level.main ? 0 : level.level * (HEIGHT_PER_OP * 0.9)

    const { operation_type, event_type, status } = data.operation;
    const { action_type, status: validationStatus } = data;
    const isTimeline = opFocusId === data.operation.id;

    const style = {
        '--op-left': start,
        '--op-width': finish - start,
        '--op-min-width': minWidth,
    }
    const operationTop = top ? top + 'px' : `calc(var(--${vesselId}-top) + ${moreHeightOfOp}px)`;

    return <>
        <div
            id={data.operation.id}
            ref={rootRef}
            data-rule={EL_RULE.OPERATION}
            data-editing={!!action_type}
            data-operation-type={data.operation.operation_type}
            data-operation-status={validationStatus}
            data-is-hover={modeDrag !== 'move'}
            data-mode-drag={modeDrag}
            className={styles.operation}
            style={{ top: operationTop, ...style }}
        >
            <Logo type={operation_type} status={realStatus || status} dataAttribute={{ 'data-event-type': event_type }} />
            <Certain operation={data.operation} status={status} isLabel={false} />
            <div className="flex text-[14px] ml-[5px] items-center whitespace-nowrap">
                {description}
            </div>
            <LoadingOperation />
        </div>
        {isTimeline && <Timeline start={start} finish={finish} top={operationTop} activityLog={activityLog} data={data} />}
    </>;
};

export default WrapperOperation;
